/** Étapes du flux, dans l'ordre. Déduites de l'état, jamais saisies. */
export const ETAPES = ['recue', 'fournisseur', 'planifiee', 'expediee', 'transit', 'livree'];
/**
 * Libellés par défaut. `fournisseur` est complété du nom réel quand il est connu.
 *
 * Ils nomment ce qu'il reste à faire, pas l'état où la commande se trouve :
 * « À envoyer » se lit plus vite que « Reçue » quand on cherche son travail du
 * jour dans la liste.
 */
export const LIBELLE_ETAPE = {
    recue: 'À envoyer',
    fournisseur: "Attente d'expédition",
    planifiee: 'Expédition planifiée',
    expediee: 'Expédiée',
    transit: 'En transit',
    livree: 'Livrée',
};
/**
 * Index du segment de progression (5 segments).
 * `planifiee` et `expediee` partagent le même segment : ce sont deux états du
 * même moment du flux — la marchandise part, avec ou sans tracking.
 */
export const SEGMENT_ETAPE = {
    recue: 0, fournisseur: 1, planifiee: 2, expediee: 2, transit: 3, livree: 4,
};
export const NOMBRE_SEGMENTS = 5;
/** Statut d'incident, séparé de la date d'expédition (cf. §3 du cahier). */
export const INCIDENTS = ['COMMANDE_MANQUANTE'];
export const TRANSPORTEURS_SUIVIS = ['Dachser', 'DSV', 'DHL', 'FedEx', 'DPD', 'Rhenus'];
/** État d'acheminement d'un envoi automatique. */
/**
 * Où en est un envoi.
 *
 * `ignore` est une DÉCISION, et c'est ce qui le sépare de `desactive` : celui-ci
 * dit qu'Outlook était indisponible — un empêchement, que la prochaine tentative
 * lèvera —, celui-là qu'on a jugé le mail inutile. Une commande partie hier et
 * déjà annoncée au téléphone n'a pas besoin d'un mail d'expédition ; sans moyen
 * de le dire, la tâche resterait dans « À faire » jusqu'à ce qu'on l'exécute
 * pour s'en débarrasser, ce qui enverrait un mail dont personne ne veut.
 *
 * Les confondre ferait disparaître une tâche pour une panne d'Outlook, ou
 * ferait réapparaître un mail qu'on a écarté.
 */
export const ETATS_MAIL = [
    'non_requis', 'a_envoyer', 'envoye', 'echec', 'desactive', 'ignore',
];
/**
 * Les trois envois automatiques, chacun avec son propre état. Ils ne se
 * déclenchent pas au même moment du flux :
 *
 * - `fournisseur` : la commande est transmise au fournisseur ;
 * - `expedition`  : la date de départ est connue, le client est prévenu ;
 * - `tracking`    : le numéro de suivi est saisi, le client reçoit son lien.
 *
 * La collection distinguait déjà `mailExpeditionEnvoye` de `mailTrackingEnvoye` :
 * un même client reçoit bien deux messages, à deux moments différents.
 */
export const CANAUX_ENVOI = ['fournisseur', 'expedition', 'tracking'];
/**
 * Tous les canaux passent par Outlook.
 *
 * Les trois mails sont composés par l'application, relus à l'écran, puis
 * ENVOYÉS depuis contact@. n8n n'a plus de rôle : le dernier scénario qu'il tenait
 * — l'annonce d'expédition — a rejoint les deux autres, et gagné au passage
 * trois langues et une date localisée.
 */
export const CANAUX_OUTLOOK = CANAUX_ENVOI;
export const LIBELLE_CANAL = {
    fournisseur: 'Commande au fournisseur',
    expedition: "Mail d'expédition",
    tracking: 'Mail de tracking',
};
/** Titre de l'action levée quand un envoi échoue. */
export const LIBELLE_ECHEC_CANAL = {
    fournisseur: 'Commande fournisseur non préparée',
    expedition: "Mail d'expédition non parti",
    tracking: 'Mail de tracking non parti',
};
/**
 * Titre de l'action levée quand un mail client reste À PRÉPARER.
 *
 * Aucun mail ne se prépare plus tout seul : rien ne part sans qu'on l'ait
 * demandé. Ce qui était un effet de bord du relevé Fortemix — une date
 * trouvée, un brouillon déposé dans la foulée — devient
 * donc une tâche, visible dans « À faire » et exécutée quand on le décide.
 *
 * Sans cette action, couper l'automatisme aurait fait disparaître le travail en
 * même temps que le geste : le client cesserait d'être prévenu, et rien ne le
 * dirait.
 */
export const LIBELLE_ATTENTE_CANAL = {
    fournisseur: 'Commande à transmettre au fournisseur',
    expedition: "Prévenir le client de la date d'expédition",
    tracking: 'Envoyer le numéro de suivi au client',
};
/**
 * Les deux canaux qui s'adressent au CLIENT ; le troisième va au fournisseur.
 *
 * Eux seuls s'ignorent. Un mail au fournisseur n'est pas une politesse qu'on
 * peut sauter : sans lui la commande n'est pas passée, et l'écarter reviendrait
 * à perdre la commande. Prévenir le client d'un départ, en revanche, se fait
 * parfois autrement — au téléphone, de vive voix — et la tâche doit alors
 * pouvoir être écartée sans qu'on envoie un mail dont personne ne veut.
 */
export const CANAUX_CLIENT = ['expedition', 'tracking'];
/**
 * Langues dans lesquelles une relance fournisseur peut partir.
 *
 * Deux suffisent : l'annuaire du service administratif ne connaît que le
 * français et l'anglais. La liste est ouverte — ajouter une langue, c'est
 * ajouter son modèle dans `RELANCE_FOURNISSEUR_PAR_DEFAUT`.
 */
export const LANGUES_RELANCE = ['fr', 'en'];
/** Fiche d'annuaire d'une société : à qui écrire, dans quelle langue. */
/** Qui l'on relance quand une commande traîne : le fournisseur, ou le transport. */
export const CIBLES_RELANCE = ['fournisseur', 'transporteur'];
/** Comment une relance a été faite, quand ce n'est pas par le mail de l'outil. */
export const MOYENS_RELANCE = ['telephone', 'mail_perso', 'courrier', 'sur_place', 'autre'];
export const LIBELLE_MOYEN_RELANCE = {
    telephone: 'par téléphone',
    mail_perso: 'par un mail envoyé à la main',
    courrier: 'par courrier',
    sur_place: 'de vive voix',
    autre: 'par un autre moyen',
};
/**
 * Comment une relance s'est faite, TOUTES origines confondues.
 *
 * `mail` est le chemin normal — un mail envoyé par l'application. Les
 * autres viennent du bouton « déjà relancé autrement ». `inconnu` n'existe que
 * pour les relances d'avant ce journal : leur compteur dit qu'elles ont eu
 * lieu, rien ne dit comment, et inventer un moyen serait pire que l'avouer.
 */
export const MOYENS_RELANCE_JOURNAL = ['mail', ...MOYENS_RELANCE, 'inconnu'];
/**
 * Ce qu'une relance réclamait.
 *
 * C'est la clé de l'ACTION qui était due au moment du geste — c'est elle qui
 * porte le sens : relancer pour une date d'expédition, pour une commande que le
 * fournisseur ne retrouve pas, ou pour une livraison qui traîne ne sont pas la
 * même démarche, et ne se relisent pas pareil.
 */
export const MOTIFS_RELANCE = ['relance', 'probleme', 'tracking', 'livraison'];
/**
 * Délai de résolution par défaut, en jours.
 *
 * Arbitrages métier, à revoir à l'usage — c'est précisément pour cela qu'ils
 * sont modifiables depuis Réglages. Une date d'expédition se rend en quelques
 * jours ; une commande égarée demande d'être repassée puis enregistrée, donc
 * plus ; une livraison bloquée se débloque vite ou pas du tout.
 *
 * Un numéro de suivi s'aligne sur la date d'expédition : c'est une information
 * que l'autre partie détient déjà, pas quelque chose qu'elle doit produire.
 */
export const RESOLUTION_LITIGE_PAR_DEFAUT = {
    relance: 3,
    probleme: 5,
    tracking: 3,
    livraison: 3,
};
export const LIBELLE_MOTIF_RELANCE = {
    relance: "la date d'expédition",
    probleme: 'une commande introuvable chez le fournisseur',
    tracking: 'le numéro de suivi',
    livraison: 'la livraison',
};
/**
 * Les actions que le moteur sait lever.
 *
 * Déclarées en tableau plutôt qu'en union de littéraux pour qu'elles soient
 * ÉNUMÉRABLES : l'écran doit pouvoir vérifier qu'il sait quoi faire de chacune.
 * Sans cette liste, ajouter une clé au moteur laissait son bouton sans effet —
 * l'utilisateur cliquait, rien ne se passait, et aucune erreur ne le disait.
 */
export const CLES_ACTION = [
    'attente', 'fournisseur', 'enlevement', 'probleme', 'shopify', 'relance', 'tracking', 'mail',
    'livraison', 'avis',
];
/**
 * Litiges d'une commande : ce qui a déraillé, et qui doit se lire depuis la
 * liste sans ouvrir la fiche.
 *
 * Deux natures cohabitent, et l'interface les distingue :
 *
 * - les **relances**, qui sont un passé — elles se comptent et ne s'effacent
 *   pas, même quand l'affaire est réglée ;
 * - les **incidents en cours**, qui sont un présent — commande introuvable,
 *   livraison bloquée, envoi automatique en échec.
 */
export const CLES_LITIGE = [
    'relance_fournisseur',
    'relance_transporteur',
    'commande_manquante',
    'livraison_bloquee',
    'envoi_echec',
];
/**
 * Vues de la liste, dans l'ordre des onglets.
 *
 * Elles remplacent le découpage par étape, qui obligeait à parcourir six
 * onglets pour savoir quoi faire. Celles-ci répondent à quatre questions :
 * qu'est-ce qui me réclame maintenant, qu'est-ce qui tourne, qu'est-ce qui a
 * déjà déraillé, et qu'est-ce qui est fini.
 */
export const VUES = ['afaire', 'encours', 'problemes', 'archive'];
export const LIBELLE_VUE = {
    afaire: 'À faire',
    encours: 'En cours',
    problemes: 'Problèmes',
    archive: 'Archive',
};
/** Ce que chaque onglet contient, dit à l'utilisateur sous le titre. */
export const DETAIL_VUE = {
    afaire: "Les commandes non livrées qui attendent un geste aujourd'hui. Une commande transmise ou "
        + 'relancée en dort le temps de ses seuils, puis revient ici.',
    encours: "Les commandes non livrées qui n'attendent rien de vous aujourd'hui : le geste a été fait. "
        + 'Elles suivent leur cours, et la colonne de droite dit ce qu’on attend plutôt que de '
        + 'proposer un bouton.',
    problemes: 'Les litiges EN COURS : non livrées, déjà relancées, et encore dans le délai laissé '
        + "à l'autre partie pour répondre. Le terme passé, elles repartent d'elles-mêmes — dans "
        + '« En cours » si la réponse est venue, dans « À faire » sinon.',
    archive: 'Les commandes livrées.',
};
/** Tri appliqué par défaut à chaque vue. */
export const TRI_PAR_DEFAUT_VUE = {
    afaire: 'urgence',
    encours: 'date',
    problemes: 'urgence',
    archive: 'date',
};
/** Groupes d'actions proposés en filtre, dans l'ordre d'affichage. */
export const GROUPES_ACTION = [
    { cle: 'probleme', titre: 'Problème fournisseur' },
    { cle: 'shopify', titre: 'Commande modifiée dans Shopify' },
    { cle: 'mail', titre: 'Envoi automatique en échec' },
    { cle: 'livraison', titre: 'Contrôler la livraison' },
    { cle: 'tracking', titre: 'Saisir le tracking' },
    { cle: 'fournisseur', titre: 'Envoyer au fournisseur' },
    { cle: 'enlevement', titre: 'Demander l’enlèvement' },
    { cle: 'relance', titre: 'Relancer le fournisseur' },
    { cle: 'avis', titre: 'Demander un avis' },
];
//# sourceMappingURL=types.js.map